// Unified Content Exporter Hook
// Provides PDF and DOCX export for all Content Studio content types

import { useCallback, useState } from 'react';
import { useDocumentGenerator, DocumentContent, DocumentSection } from './useDocumentGenerator';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Import types
import type {
  FlashcardDeck,
  MindMap,
  MindMapNode,
  Quiz,
  QuizGradingResult,
  StudyGuide,
  SummaryResponse,
  FAQResponse,
  Timeline,
  BriefingDoc,
  PodcastResult,
  DocumentAnalysis,
} from '@/lib/content-studio/types';

// Import PDF document component
import { ContentPDFDocument } from '@/components/ContentStudio/PDFTemplates';

export type ExportContentType = 
  | 'analyze'
  | 'flashcards'
  | 'mindmap'
  | 'quiz'
  | 'quiz-results'
  | 'study-guide'
  | 'summary'
  | 'faq'
  | 'timeline'
  | 'briefing-doc'
  | 'podcast-transcript';

export function useContentExporter() {
  const { generateDocx } = useDocumentGenerator();
  const [isExporting, setIsExporting] = useState(false);

  // Format document analysis for export
  const formatAnalysis = (analysis: DocumentAnalysis): DocumentContent => {
    const sections: DocumentSection[] = [];

    // Summary
    sections.push({
      id: 'summary',
      name: 'Summary',
      type: 'text',
      content: analysis.summary,
    });

    // Key topics
    if (analysis.keyTopics?.length) {
      sections.push({
        id: 'topics',
        name: 'Key Topics',
        type: 'table',
        content: analysis.keyTopics.map(topic => ({
          'Topic': topic.topic,
          'Description': topic.description,
          'Importance': topic.importance,
        })),
      });
    }

    // Main points
    if (analysis.mainPoints?.length) {
      sections.push({
        id: 'main-points',
        name: 'Main Points',
        type: 'list',
        content: analysis.mainPoints,
      });
    }

    // Terminology
    if (analysis.terminology?.length) {
      sections.push({
        id: 'terminology',
        name: 'Key Terminology',
        type: 'key_value',
        content: analysis.terminology.map(term => ({
          key: term.term,
          value: term.definition,
        })),
      });
    }

    // Suggested questions
    if (analysis.suggestedQuestions?.length) {
      sections.push({
        id: 'questions',
        name: 'Suggested Questions',
        type: 'numbered_list',
        content: analysis.suggestedQuestions,
      });
    }

    return {
      title: analysis.title,
      subtitle: `${analysis.complexity} level • ${analysis.estimatedReadTime} read`,
      generatedAt: new Date().toISOString(),
      sections,
    };
  };

  // Format flashcards for document export
  const formatFlashcards = (deck: FlashcardDeck): DocumentContent => {
    const sections: DocumentSection[] = [];

    // Add description
    if (deck.description) {
      sections.push({
        id: 'description',
        name: 'About This Deck',
        type: 'text',
        content: deck.description,
      });
    }

    // Add cards by category
    const categorized = deck.cards.reduce((acc, card) => {
      const cat = card.category || 'General';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(card);
      return acc;
    }, {} as Record<string, typeof deck.cards>);

    Object.entries(categorized).forEach(([category, cards]) => {
      sections.push({
        id: `category-${category}`,
        name: category,
        type: 'key_value',
        content: cards.map(card => ({
          key: card.front,
          value: card.back,
        })),
      });
    });

    return {
      title: deck.title,
      subtitle: `${deck.totalCards} Flashcards`,
      generatedAt: new Date().toISOString(),
      sections,
    };
  };

  // Format mind map for document export
  const formatMindMap = (mindMap: MindMap): DocumentContent => {
    const sections: DocumentSection[] = [];

    // Central topic
    sections.push({
      id: 'central',
      name: 'Central Topic',
      type: 'text',
      content: mindMap.centralTopic,
    });

    // Flatten nodes recursively
    const formatNode = (node: MindMapNode, prefix: string = ''): string[] => {
      const result: string[] = [`${prefix}${node.label}${node.description ? `: ${node.description}` : ''}`];
      if (node.children) {
        node.children.forEach(child => {
          result.push(...formatNode(child, prefix + '  • '));
        });
      }
      return result;
    };

    // Main branches
    mindMap.nodes.forEach((node, idx) => {
      const nodeContent = formatNode(node);
      sections.push({
        id: `branch-${idx}`,
        name: node.label,
        type: 'list',
        content: nodeContent.slice(1), // Skip the main label
      });
    });

    return {
      title: mindMap.title,
      subtitle: `${mindMap.nodes.length} Main Topics`,
      generatedAt: new Date().toISOString(),
      sections,
    };
  };

  // Format quiz for document export
  const formatQuiz = (quiz: Quiz, results?: QuizGradingResult): DocumentContent => {
    const sections: DocumentSection[] = [];

    // Quiz info
    sections.push({
      id: 'info',
      name: 'Quiz Information',
      type: 'key_value',
      content: [
        { key: 'Total Questions', value: quiz.totalQuestions.toString() },
        { key: 'Estimated Time', value: quiz.estimatedTime },
        ...(results ? [
          { key: 'Score', value: `${results.percentage}%` },
          { key: 'Correct Answers', value: `${results.correctAnswers}/${results.totalQuestions}` },
          { key: 'Status', value: results.passed ? 'Passed' : 'Needs Improvement' },
        ] : []),
      ],
    });

    // Questions
    quiz.questions.forEach((q, idx) => {
      const questionResult = results?.results?.find(r => r.questionId === q.id);
      let content = q.question;
      
      if (q.scenarioContext) {
        content = `Context: ${q.scenarioContext}\n\n${q.question}`;
      }

      if (q.options) {
        content += '\n\nOptions:\n' + q.options.map((opt, i) => `${String.fromCharCode(65 + i)}. ${opt}`).join('\n');
      }

      if (results && questionResult) {
        content += `\n\nYour Answer: ${questionResult.userAnswer}`;
        content += `\nCorrect Answer: ${questionResult.correctAnswer}`;
        content += questionResult.isCorrect ? '\n✓ Correct' : '\n✗ Incorrect';
      }

      content += `\n\nExplanation: ${q.explanation}`;

      sections.push({
        id: `q-${idx}`,
        name: `Question ${idx + 1} (${q.difficulty})`,
        type: 'text',
        content,
      });
    });

    return {
      title: results ? `${quiz.title} - Results` : quiz.title,
      subtitle: quiz.description,
      generatedAt: new Date().toISOString(),
      sections,
    };
  };

  // Format study guide for document export
  const formatStudyGuide = (guide: StudyGuide): DocumentContent => {
    const sections: DocumentSection[] = [];

    // Overview
    sections.push({
      id: 'overview',
      name: 'Overview',
      type: 'text',
      content: guide.overview,
    });

    // Learning objectives
    if (guide.learningObjectives?.length) {
      sections.push({
        id: 'objectives',
        name: 'Learning Objectives',
        type: 'numbered_list',
        content: guide.learningObjectives,
      });
    }

    // Main sections
    guide.sections?.forEach((section, idx) => {
      sections.push({
        id: `section-${idx}`,
        name: section.title,
        type: 'text',
        content: section.content,
      });

      if (section.keyPoints?.length) {
        sections.push({
          id: `section-${idx}-keypoints`,
          name: `${section.title} - Key Points`,
          type: 'list',
          content: section.keyPoints,
        });
      }
    });

    // Quick reference
    if (guide.quickReference) {
      sections.push({
        id: 'quick-ref',
        name: 'Quick Reference',
        type: 'text',
        content: guide.quickReference.summary,
      });

      if (guide.quickReference.keyFacts?.length) {
        sections.push({
          id: 'key-facts',
          name: 'Key Facts',
          type: 'list',
          content: guide.quickReference.keyFacts,
        });
      }
    }

    // Glossary
    if (guide.glossary?.length) {
      sections.push({
        id: 'glossary',
        name: 'Glossary',
        type: 'key_value',
        content: guide.glossary.map(item => ({
          key: item.term,
          value: item.definition,
        })),
      });
    }

    return {
      title: guide.title,
      generatedAt: new Date().toISOString(),
      sections,
    };
  };

  // Format summary for document export
  const formatSummary = (summary: SummaryResponse): DocumentContent => {
    const sections: DocumentSection[] = [];

    sections.push({
      id: 'executive',
      name: 'Executive Summary',
      type: 'text',
      content: summary.executiveSummary,
    });

    sections.push({
      id: 'key-points',
      name: 'Key Points',
      type: 'list',
      content: summary.keyPoints,
    });

    summary.sections.forEach((section, idx) => {
      sections.push({
        id: `section-${idx}`,
        name: section.heading,
        type: 'text',
        content: section.content,
      });
    });

    return {
      title: summary.title,
      subtitle: `${summary.wordCount} words`,
      generatedAt: new Date().toISOString(),
      sections,
    };
  };

  // Format FAQ for document export
  const formatFAQ = (faq: FAQResponse): DocumentContent => {
    const sections: DocumentSection[] = [];

    // Group by category
    const byCategory = faq.items.reduce((acc, item) => {
      const cat = item.category || 'General';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {} as Record<string, typeof faq.items>);

    Object.entries(byCategory).forEach(([category, items]) => {
      sections.push({
        id: `cat-${category}`,
        name: category,
        type: 'key_value',
        content: items.map(item => ({
          key: item.question,
          value: item.answer,
        })),
      });
    });

    return {
      title: faq.title,
      subtitle: faq.description,
      generatedAt: new Date().toISOString(),
      sections,
    };
  };

  // Format timeline for document export
  const formatTimeline = (timeline: Timeline): DocumentContent => {
    const sections: DocumentSection[] = [];

    sections.push({
      id: 'description',
      name: 'Overview',
      type: 'text',
      content: timeline.description,
    });

    // Events as a table
    sections.push({
      id: 'events',
      name: 'Events',
      type: 'table',
      content: timeline.events.map(event => ({
        'Date/Period': event.date || event.period || '',
        'Event': event.title,
        'Description': event.description,
        'Significance': event.significance,
      })),
    });

    return {
      title: timeline.title,
      generatedAt: new Date().toISOString(),
      sections,
    };
  };

  // Format briefing doc for document export
  const formatBriefingDoc = (doc: BriefingDoc): DocumentContent => {
    const sections: DocumentSection[] = [];

    sections.push({
      id: 'executive',
      name: 'Executive Summary',
      type: 'text',
      content: doc.executiveSummary,
    });

    sections.push({
      id: 'background',
      name: 'Background',
      type: 'text',
      content: doc.background,
    });

    sections.push({
      id: 'findings',
      name: 'Key Findings',
      type: 'table',
      content: doc.keyFindings.map(f => ({
        'Finding': f.finding,
        'Importance': f.importance,
        'Details': f.details || '',
      })),
    });

    sections.push({
      id: 'recommendations',
      name: 'Recommendations',
      type: 'numbered_list',
      content: doc.recommendations,
    });

    if (doc.nextSteps?.length) {
      sections.push({
        id: 'next-steps',
        name: 'Next Steps',
        type: 'numbered_list',
        content: doc.nextSteps,
      });
    }

    return {
      title: doc.title,
      subtitle: doc.preparedFor ? `Prepared for: ${doc.preparedFor}` : undefined,
      generatedAt: doc.date,
      sections,
    };
  };

  // Format podcast transcript for document export
  const formatPodcastTranscript = (podcast: PodcastResult): DocumentContent => {
    const sections: DocumentSection[] = [];

    sections.push({
      id: 'info',
      name: 'Podcast Information',
      type: 'key_value',
      content: [
        { key: 'Title', value: podcast.script.title },
        { key: 'Description', value: podcast.script.description },
        { key: 'Duration', value: podcast.script.totalDuration || 'Not specified' },
        { key: 'Segments', value: podcast.script.segments.length.toString() },
      ],
    });

    // Full transcript
    const transcript = podcast.script.segments.map(seg => 
      `[${seg.speakerName}]\n${seg.text}`
    ).join('\n\n');

    sections.push({
      id: 'transcript',
      name: 'Full Transcript',
      type: 'text',
      content: transcript,
    });

    return {
      title: `${podcast.script.title} - Transcript`,
      generatedAt: new Date().toISOString(),
      sections,
    };
  };

  // Main export to DOCX function
  const exportToDocx = useCallback(async (
    contentType: ExportContentType,
    data: any,
    results?: QuizGradingResult
  ) => {
    setIsExporting(true);
    try {
      let documentContent: DocumentContent;

      switch (contentType) {
        case 'analyze':
          documentContent = formatAnalysis(data as DocumentAnalysis);
          break;
        case 'flashcards':
          documentContent = formatFlashcards(data as FlashcardDeck);
          break;
        case 'mindmap':
          documentContent = formatMindMap(data as MindMap);
          break;
        case 'quiz':
        case 'quiz-results':
          documentContent = formatQuiz(data as Quiz, results);
          break;
        case 'study-guide':
          documentContent = formatStudyGuide(data as StudyGuide);
          break;
        case 'summary':
          documentContent = formatSummary(data as SummaryResponse);
          break;
        case 'faq':
          documentContent = formatFAQ(data as FAQResponse);
          break;
        case 'timeline':
          documentContent = formatTimeline(data as Timeline);
          break;
        case 'briefing-doc':
          documentContent = formatBriefingDoc(data as BriefingDoc);
          break;
        case 'podcast-transcript':
          documentContent = formatPodcastTranscript(data as PodcastResult);
          break;
        default:
          throw new Error(`Unsupported content type: ${contentType}`);
      }

      // Generate proper filename with content type
      const filename = generateFilename(data, contentType, 'docx');
      await generateDocx(documentContent, filename);
      toast.success('Word document downloaded!');
    } catch (error) {
      console.error('DOCX export failed:', error);
      toast.error('Failed to export document');
    } finally {
      setIsExporting(false);
    }
  }, [generateDocx]);

  // Get human-readable content type label
  const getContentTypeLabel = (type: ExportContentType): string => {
    const labels: Record<ExportContentType, string> = {
      'analyze': 'Analysis',
      'flashcards': 'Flashcards',
      'mindmap': 'Mind-Map',
      'quiz': 'Quiz',
      'quiz-results': 'Quiz-Results',
      'study-guide': 'Study-Guide',
      'summary': 'Summary',
      'faq': 'FAQ',
      'timeline': 'Timeline',
      'briefing-doc': 'Briefing',
      'podcast-transcript': 'Podcast-Transcript',
    };
    return labels[type] || type;
  };

  // Generate filename with title and content type
  const generateFilename = (data: any, contentType: ExportContentType, extension: string): string => {
    const title = data.title || data.script?.title || 'Document';
    const typeLabel = getContentTypeLabel(contentType);
    const dateStr = format(new Date(), 'yyyy-MM-dd');
    
    // Clean the title: remove special chars, replace spaces with hyphens
    const cleanTitle = title
      .replace(/[^a-zA-Z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 50); // Limit length
    
    return `${cleanTitle}_${typeLabel}_${dateStr}.${extension}`;
  };

  // Main export to PDF function
  const exportToPdf = useCallback(async (
    contentType: ExportContentType,
    data: any,
    results?: QuizGradingResult
  ) => {
    setIsExporting(true);
    try {
      const blob = await pdf(
        ContentPDFDocument({ type: contentType, data, results })
      ).toBlob();
      
      const filename = generateFilename(data, contentType, 'pdf');
      
      saveAs(blob, filename);
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  }, []);

  return {
    exportToDocx,
    exportToPdf,
    isExporting,
  };
}

export default useContentExporter;
